import { useRouter } from 'next/router'
import Script from 'next/script'
import { useSession } from 'next-auth/react'
import { useTranslation } from 'next-i18next'
import { type FC, useEffect, useState } from 'react'
import { useTheme } from 'styled-components'

import { ShowAtOrLarger, ShowWhenSmaller } from '@/components/shared/breakpoint'
import { LinkCustom } from '@/components/shared/link-custom/LinkCustom'
import { AutocanBadge } from '@/components/vip/vip-autocan-features/AutocanBadge'
import {
  type AutocanFeatures,
  AUTOCAN_CONFIG_PER_FEATURE_TYPE,
} from '@/components/vip/vip-autocan-features/utils'
/**
 * TODO: Cleanup the autocan variable names
 * TODO: Figure out what is the difference between ICO and Value Estimate
 */
import { AC_URL, AUTOCAN_CTA_IDS, ICO_UTM_PARAMS } from '@/constants/ico'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { getAutocanWarrantyUrl } from '@/domain/autocanada/getAutocanWarrantyUrl'
import { openAutocanValueEstimateModal } from '@/domain/autocanada/openAutocanValueEstimateModal'
import { getLocationPath } from '@/domain/location/getLocationPath'
import { useLocale } from '@/hooks/useLocale'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { type VipQueryListing } from '@/types/listings'
import { getIcoScriptURL } from '@/utils/autoCanadaHelper'
import { getUserDevice } from '@/utils/getUserDevice'
import { sendToLogger } from '@/utils/sendToLogger'

export type AutocanFeatureBadgeProps = {
  listing: VipQueryListing
  useInlineVariant: boolean
  userAgent: string
  variant: AutocanFeatures
}

/**
 * This component contains the logic for each autocan feature "warning", "value-estimate" & "financing"
 * It handles tracking for each variant, external link implementation and labels
 */
export const AutocanFeatureBadge: FC<AutocanFeatureBadgeProps> = ({
  listing,
  useInlineVariant,
  userAgent,
  variant,
}) => {
  const { colors } = useTheme()
  const { data } = useSession()
  const { push } = useRouter()
  const { routeLocale } = useLocale()
  const { t } = useTranslation(TRANSLATION_KEYS.VIP)

  const [externalLink, setExternalLink] = useState<string>('')

  const mainImageSrc = listing.imageUrls[0]
  const provinceId = getLocationPath(listing.location.id)[1].id
  const isMobile = getUserDevice(userAgent).isPhone

  const config = AUTOCAN_CONFIG_PER_FEATURE_TYPE({
    attributes: listing.attributes?.all,
    colors,
    mainImageSrc,
    t,
  })[variant]

  useEffect(() => {
    if (externalLink) return

    if (variant === 'warranty') {
      const externalLink = getAutocanWarrantyUrl({
        isMobile,
        listing,
        provinceId,
        routeLocale,
        userId: data?.user.sub,
      })

      setExternalLink(externalLink)
    }
  }, [data?.user.sub, externalLink, isMobile, listing, provinceId, routeLocale, variant])

  const handleFeatureClick = () => {
    if (variant === 'warranty') {
      trackEvent({
        action: GA_EVENT.R2SExternalBegin,
        label: 'btn=FnWURL',
        customParameters: { eventCategory: 'VIP' },
      })
    }

    if (variant === 'value-estimate') {
      openAutocanValueEstimateModal({
        attributes: listing.attributes?.all,
        ctaId: AUTOCAN_CTA_IDS.VALUE_ESTIMATE_VIP,
        customGAParameters: { eventCategory: 'VIP' },
        isMobile,
        provinceId,
        routeLocale,
        user: data?.user,
        utmContent: ICO_UTM_PARAMS.UTM_CONTENT_ESTIMATE_VIP,
        push,
      })

      trackEvent({
        action: GA_EVENT.ValueEstimateBegin,
        customParameters: { eventCategory: 'VIP' },
      })
    }

    if (variant === 'financing') {
      trackEvent({
        action: GA_EVENT.R2SExternalBegin,
        label: 'btn=FnIURL',
        customParameters: { eventCategory: 'VIP' },
      })
    }
  }

  return (
    <>
      {variant === 'value-estimate' ? (
        // Only initialize the script if the value-estimate banner is showing
        <Script
          id={AC_URL.ICO_SCRIPT_ID}
          onError={(err) => sendToLogger(err, { fingerprint: ['ValueEstimate'] })}
          src={getIcoScriptURL()}
          strategy="lazyOnload"
        />
      ) : null}

      <ShowWhenSmaller breakpoint="medium">
        <LinkCustom
          as={externalLink ? 'a' : 'button'}
          data-testid="autocan-badge-link"
          href={externalLink}
          noStyle
          onClick={handleFeatureClick}
          style={{ width: '100%', textDecoration: 'none' }}
          target="_blank"
        >
          <AutocanBadge
            {...config}
            handleFeatureClick={handleFeatureClick}
            isMobileVersion
            useInlineVariant={useInlineVariant}
          />
        </LinkCustom>
      </ShowWhenSmaller>

      <ShowAtOrLarger breakpoint="medium">
        <AutocanBadge
          {...config}
          useInlineVariant={useInlineVariant}
          handleFeatureClick={handleFeatureClick}
          externalLink={externalLink}
        />
      </ShowAtOrLarger>
    </>
  )
}
